import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleLanguage = () => {
    const nextLang = i18n.language === 'zh' ? 'en' : 'zh';
    i18n.changeLanguage(nextLang);
  };

  const isEn = i18n.language === 'en';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0a0118]/95 backdrop-blur-md shadow-lg shadow-purple-500/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png"
              alt="Pixel Monster Logo"
              className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-110"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white font-['Space_Grotesk'] tracking-tight">
                像素怪兽
              </span>
              <span className="text-xs text-[#00ff41] font-['JetBrains_Mono'] tracking-wider">
                PIXEL MONSTER
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('about')}
              className="text-sm font-['JetBrains_Mono'] text-gray-300 hover:text-[#00ff41] transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
            >
              {t('nav.about')}
              <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#00ff41] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-sm font-['JetBrains_Mono'] text-gray-300 hover:text-[#00ff41] transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
            >
              {t('nav.services')}
              <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#00ff41] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button
              onClick={() => scrollToSection('cases')}
              className="text-sm font-['JetBrains_Mono'] text-gray-300 hover:text-[#00ff41] transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
            >
              {t('nav.cases')}
              <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#00ff41] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button
              onClick={() => scrollToSection('roadmap')}
              className="text-sm font-['JetBrains_Mono'] text-gray-300 hover:text-[#00ff41] transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
            >
              {t('nav.roadmap')}
              <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#00ff41] transition-all duration-300 group-hover:w-full"></span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="relative flex items-center gap-1.5 px-3 py-2 font-['JetBrains_Mono'] text-xs font-bold transition-all duration-300 cursor-pointer border whitespace-nowrap overflow-hidden group"
              style={{
                borderColor: '#00ff41',
                color: isEn ? '#000' : '#00ff41',
                backgroundColor: isEn ? '#00ff41' : 'transparent',
                clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
              }}
              title={isEn ? '切换到中文' : 'Switch to English'}
            >
              <span
                className="relative z-10 flex items-center gap-1"
                style={{ color: isEn ? '#000' : '#00ff41' }}
              >
                <span
                  className="transition-all duration-300"
                  style={{ opacity: isEn ? 0.5 : 1 }}
                >
                  中
                </span>
                <span className="opacity-40">/</span>
                <span
                  className="transition-all duration-300"
                  style={{ opacity: isEn ? 1 : 0.5 }}
                >
                  EN
                </span>
              </span>
            </button>

            {/* CTA Button */}
            <button
              onClick={() => scrollToSection('contact')}
              className="px-6 py-2.5 bg-transparent border-2 border-[#00ff41] text-[#00ff41] font-['JetBrains_Mono'] text-sm font-semibold hover:bg-[#00ff41] hover:text-black transition-all duration-300 relative overflow-hidden group whitespace-nowrap cursor-pointer"
              style={{
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
              }}
            >
              <span className="relative z-10">{t('nav.cta')}</span>
              <div className="absolute inset-0 bg-[#00ff41] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
